<template>
  <header class="page-header-container">
    <div class="header-content">
      <!-- Logo Section -->
      <div class="header-section logo-section">
        <Logo @click="handleLogoClick" />
      </div>

      <!-- Navigation Links Section -->
      <div class="header-section nav-section center-mid">
        <NavigationTabs :prefix="prefix" />
      </div>

      <!-- Search and Icons Section -->
      <div class="header-section auth-section">
        <div class="auth-buttons">
          <div class="search-container" :class="{ 'expanded': isSearchActive }" @click="toggleSearch">
            <img src="/icons/search_icon.png" alt="Search" class="search-icon" />
            <input
              ref="searchInput"
              type="text"
              placeholder="Search"
              v-model="searchQuery"
              @keyup.enter="performSearch"
              @blur="handleSearchBlur"
              @click.stop
              class="search-input"
            />
          </div>
          <Icons :prefix="prefix" />
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router';
import { defineProps, ref, nextTick } from 'vue';
import Logo from './Logo.vue';
import NavigationTabs from './NavigationTabs.vue';
import Icons from './Icons.vue';

// Define props
const props = defineProps({
  prefix: String,
});

const router = useRouter();
const route = useRoute();

// Search toggle functionality
const isSearchActive = ref(false);
const searchQuery = ref('');
const searchInput = ref<HTMLInputElement | null>(null);

const toggleSearch = async () => {
  isSearchActive.value = !isSearchActive.value;
  if (isSearchActive.value) {
    await nextTick();
    searchInput.value?.focus();
  }
};

const handleSearchBlur = () => {
  // Delay hiding to allow for click events
  setTimeout(() => {
    if (!searchQuery.value.trim()) {
      isSearchActive.value = false;
    }
  }, 200);
};

const performSearch = () => {
  if (searchQuery.value.trim()) {
    router.push(`/field/search?q=${encodeURIComponent(searchQuery.value)}`);
  } else {
    router.push(`/field/search`);
  }
  isSearchActive.value = false;
};

// Handle logo click
const handleLogoClick = () => {
  const targetPath = `/${props.prefix}/feed`;

  // If current path matches the target path, redirect to '/'
  if (route.path === targetPath) {
    router.push('/');
  } else {
    router.push(targetPath);
  }
};
</script>

<style scoped>
/* Root custom property for header height */
:root {
  --header-height: 50px;
}

.page-header-container {
  padding: 10px 40px;
  background-color: transparent;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--header-height);
  display: flex;
  align-items: center;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1360px;
  width: 100%;
  margin: 0 auto;
}

.header-section {
  background-color: rgba(235, 235, 235, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 999px;
  display: flex;
  align-items: center;
  opacity: 1;
  padding: 6px;
  border: 1px solid rgba(235, 235, 235, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

/* Logo specific styles */
.logo-section {
  padding-right: 0;
  background-color: rgba(235, 235, 235, 0.7);
}

/* Navigation styles */
.nav-section {
  gap: 6px;
}

.center-mid {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

/* Auth styles */
.auth-section {
  gap: 6px;
}

.auth-buttons {
  display: flex;
  gap: 6px;
  align-items: center;
}

/* Search container styles */
.search-container {
  display: flex;
  align-items: center;
  background-color: #fff;
  border-radius: 25px;
  border: 1px solid rgba(0, 0, 0, 0.5);
  padding: 5px 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 30px;
  overflow: hidden;
}

.search-container.expanded {
  min-width: 200px;
}

.search-container:hover {
  background-color: #fff;
  border-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.search-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  margin-right: 0;
  flex-shrink: 0;
  transition: margin-right 0.3s ease;
}

.search-container.expanded .search-icon {
  margin-right: 8px;
}

.search-input {
  border: none;
  background: transparent;
  outline: none;
  flex: 1;
  font-size: 12px;
  color: #b8b8b8;
  text-align: left;
  width: 0;
  opacity: 0;
  padding: 0;
  margin: 0;
  transition: all 0.3s ease;
  overflow: hidden;
}

.search-container.expanded .search-input {
  width: 150px;
  opacity: 1;
  padding: 0 5px;
}

.search-input::placeholder {
  color: #b8b8b8;
}

/* Mobile styles */
@media (max-width: 768px) {
  .page-header-container {
    padding: 8px 16px;
    height: 60px;
  }
  
  .header-content {
    justify-content: space-between;
    align-items: center;
    position: relative;
  }
  
  /* Show navigation on mobile with adjusted positioning */
  .nav-section {
    position: absolute;
    left: 50%;
    top: 200%;
    transform: translate(-50%, -50%);
    background: rgba(235, 235, 235, 0.7);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border-radius: 999px;
    padding: 5px;
    border: 1px solid rgba(235, 235, 235, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
  
  /* Simplify logo section */
  .logo-section {
    padding: 5px;
    border: none;
    box-shadow: none;
  }
  
  /* Simplify auth section */
  .auth-section {
    padding: 5px;
    border: none;
    box-shadow: none;
    position: absolute;
    right: 0px;
    top: -5px;
    z-index: 1000;
  }
  
  /* Mobile search styles */
  .search-container {
    background: rgba(235, 235, 235, 0.7);
    border: none;
    border-radius: 20px;
    min-width: 30px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 0, 0, 0.5);
  }
  
  .search-container.expanded {
    min-width: 200px;
    width: 200px;
    background: white;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .search-icon {
    width: 18px;
    height: 18px;
    margin-right: 0;
  }
  
  .search-container.expanded .search-icon {
    margin-right: 8px;
  }
  
  .search-input {
    font-size: 14px;
  }
  
  .search-container.expanded .search-input {
    width: 140px;
    font-size: 14px;
  }
}

/* Responsive styles for larger screens */
@media (max-width: 900px) and (min-width: 769px) {
  .header-content {
    flex-direction: column;
    gap: 10px;
  }
  
  .header-section {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 600px) and (min-width: 769px) {
  .auth-buttons {
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
  }
  
  .page-header-container {
    padding: 10px 20px;
  }
}
</style>
